import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, Cell,
} from 'recharts';
import {
  Loader2, Users, FileText, MessageSquare, CheckCircle2,
  Sparkles, AlertTriangle, RefreshCw, TrendingUp, UsersRound, HardDrive, Calendar, Tag, Download,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtBytes = (n) => {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

/**
 * Admin-only org-wide insights dashboard.
 * Pulls /api/admin/insights and renders headline stats + charts + action lists.
 */
const InsightsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setError('');
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/insights`, {
        withCredentials: true,
      });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load insights.');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/insights/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      // Pull filename out of Content-Disposition if present, else fall back
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      a.download = match ? match[1] : `TMC_Executive_Summary_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Could not generate PDF: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin text-[#7A8B7A]" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        {error}
      </div>
    );
  }

  if (!data) return null;
  const t = data.totals;

  return (
    <div className="space-y-8" data-testid="insights-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-medium text-[#2C302E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Insights
          </h2>
          <p className="text-sm text-[#5C635A] mt-1">
            Org-wide usage and analytics. Generated {new Date(data.generated_at).toLocaleString()}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={downloadPdf}
            disabled={downloading}
            className="flex items-center gap-2 px-3 py-2 bg-[#7A8B7A] hover:bg-[#637363] text-white rounded-full text-sm transition-colors disabled:opacity-60"
            data-testid="insights-download-pdf"
            title="Download executive summary as PDF"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {downloading ? 'Generating…' : 'Download PDF'}
          </button>
          <button
            type="button"
            onClick={() => { setLoading(true); load(); }}
            className="flex items-center gap-2 px-3 py-2 border border-[#E5E0D8] rounded-full text-sm text-[#5C635A] hover:bg-[#F4F1EA] transition-colors"
            data-testid="insights-refresh"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Top-line stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat icon={<FileText size={18} />} label="Surveys" value={t.surveys} hint={`${t.active_surveys} active · ${t.inactive_surveys} inactive · ${t.shared_surveys} shared via groups`} />
        <Stat icon={<Users size={18} />} label="Users" value={t.users} hint={`${t.admins} admin · ${t.creators} creator · +${t.new_users_30d} in 30d`} />
        <Stat icon={<MessageSquare size={18} />} label="Responses" value={t.responses} hint={`${t.responses_24h} in last 24 h · ${t.responses_7d} this week`} />
        <Stat icon={<CheckCircle2 size={18} />} label="Completion rate" value={`${t.completion_rate}%`} hint="required questions answered" />
      </div>

      {/* Response volume windows — answer the "how are we trending?" question at a glance */}
      <Card title="Response volume" subtitle="Total responses received within each lookback window.">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <MiniStat label="Last 24 h" value={t.responses_24h} />
          <MiniStat label="Last 7 days" value={t.responses_7d} />
          <MiniStat label="Last 30 days" value={t.responses_30d} />
          <MiniStat label="Last 60 days" value={t.responses_60d} />
          <MiniStat label="Last 90 days" value={t.responses_90d} />
        </div>
      </Card>

      {/* Responses over time */}
      <Card title="Responses — last 30 days" subtitle={`${t.responses_30d} responses · ${t.responses_7d} this week · ${t.responses_24h} today`}>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.daily} margin={{ top: 10, right: 16, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
              <XAxis dataKey="date" stroke="#8E958C" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#8E958C" tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: 'white', border: '1px solid #E5E0D8', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: '#2C302E' }}
              />
              <Line type="monotone" dataKey="count" stroke="#7A8B7A" strokeWidth={2} dot={{ r: 2, fill: '#7A8B7A' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Two-column: by-day-of-week + top creators */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Responses by day of week" subtitle="Across all time — useful for scheduling reminders">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.by_day_of_week} margin={{ top: 10, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                <XAxis dataKey="day" stroke="#8E958C" tick={{ fontSize: 11 }} />
                <YAxis stroke="#8E958C" tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: 'white', border: '1px solid #E5E0D8', borderRadius: 8, fontSize: 12 }}
                />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {data.by_day_of_week.map((row, i) => (
                    <Cell key={i} fill={row.count >= Math.max(...data.by_day_of_week.map(r => r.count), 1) ? '#7A8B7A' : '#C67D63'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Surveys & responses by creator" subtitle="Full ownership ledger — useful for distributing work and spotting power users.">
          {data.surveys_by_creator.length === 0 ? (
            <Empty text="No creators with surveys yet." />
          ) : (
            <div className="max-h-72 overflow-y-auto -mx-2 px-2">
              <table className="w-full text-sm">
                <thead className="text-xs text-[#8E958C] uppercase tracking-wide">
                  <tr>
                    <th className="text-left font-medium py-2">Creator</th>
                    <th className="text-right font-medium py-2">Surveys</th>
                    <th className="text-right font-medium py-2">Responses</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E0D8]">
                  {data.surveys_by_creator.map((c, i) => (
                    <tr key={i} data-testid={`creator-row-${i}`}>
                      <td className="py-2 text-[#2C302E] truncate">{c.email}</td>
                      <td className="py-2 text-right text-[#5C635A]">{c.surveys}</td>
                      <td className="py-2 text-right text-[#5C635A]">{c.responses}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Group usage — surveys per group, members, responses, recency */}
      <Card
        title="Group usage"
        subtitle="How each group is actually being used: who's in it, how many surveys are shared with it, and how much traffic that's driving."
      >
        {data.groups_summary.length === 0 ? (
          <Empty text="No groups yet. Create one from the Groups tab to start sharing surveys." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-[#8E958C] uppercase tracking-wide">
                <tr>
                  <th className="text-left font-medium py-2">Group</th>
                  <th className="text-right font-medium py-2">Members</th>
                  <th className="text-right font-medium py-2">Tagged surveys</th>
                  <th className="text-right font-medium py-2">Responses</th>
                  <th className="text-right font-medium py-2">Last activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E0D8]">
                {data.groups_summary.map((g) => (
                  <tr key={g.id} data-testid={`group-summary-${g.id}`}>
                    <td className="py-2.5 text-[#2C302E]">
                      <span className="inline-flex items-center gap-1.5">
                        <Tag size={12} className="text-[#7A8B7A]" />
                        {g.name}
                      </span>
                      {g.description && (
                        <span className="block text-xs text-[#8E958C]">{g.description}</span>
                      )}
                    </td>
                    <td className="py-2.5 text-right text-[#5C635A]">{g.member_count}</td>
                    <td className="py-2.5 text-right text-[#5C635A]">{g.survey_count}</td>
                    <td className="py-2.5 text-right text-[#5C635A]">{g.responses}</td>
                    <td className="py-2.5 text-right text-[#8E958C] text-xs">
                      {g.last_response_at ? new Date(g.last_response_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(data.empty_groups.length > 0 || data.unused_groups.length > 0) && (
          <div className="mt-4 p-3 bg-[#FDF8F4] border border-[#E5D9C8] rounded-lg text-xs text-[#5C635A] flex items-start gap-2">
            <AlertTriangle size={14} className="text-[#C67D63] flex-shrink-0 mt-0.5" />
            <span>
              {data.empty_groups.length > 0 && (
                <><strong>{data.empty_groups.length}</strong> group{data.empty_groups.length === 1 ? '' : 's'} with no members{data.unused_groups.length > 0 ? '; ' : '. '}</>
              )}
              {data.unused_groups.length > 0 && (
                <><strong>{data.unused_groups.length}</strong> group{data.unused_groups.length === 1 ? '' : 's'} not tagged on any survey. </>
              )}
              Consider cleaning these up to keep the picker tidy.
            </span>
          </div>
        )}
      </Card>

      {/* Top surveys */}
      <Card title="Top 5 surveys by responses" subtitle="The ones doing the heavy lifting">
        {data.top_surveys.length === 0 ? (
          <Empty text="No responses yet — share a survey link or QR to get started." />
        ) : (
          <ul className="divide-y divide-[#E5E0D8]">
            {data.top_surveys.map((s) => (
              <li key={s.id} className="flex items-center justify-between py-3">
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/admin/surveys/${s.id}/analytics`}
                    className="text-sm text-[#2C302E] hover:text-[#7A8B7A] truncate block"
                  >
                    {s.name}
                  </Link>
                  <p className="text-xs text-[#8E958C]">
                    {s.responses} response{s.responses === 1 ? '' : 's'} · {s.questions_count} question{s.questions_count === 1 ? '' : 's'}
                    {s.creator_email ? ` · ${s.creator_email}` : ''}
                  </p>
                </div>
                {!s.active && (
                  <span className="text-[10px] uppercase tracking-wide bg-[#C67D63]/15 text-[#9c5340] px-2 py-0.5 rounded">
                    inactive
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Action lists */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card
          title="Dormant active surveys"
          subtitle="Active but no responses in the last 30 days — maybe rotate them out or re-share."
          accent="warning"
        >
          {data.dormant_surveys.length === 0 ? (
            <Empty text="Everything active has had recent responses. Nice." />
          ) : (
            <ul className="divide-y divide-[#E5E0D8]">
              {data.dormant_surveys.map((s) => (
                <li key={s.id} className="flex items-center justify-between py-2.5">
                  <Link to={`/admin/surveys/${s.id}/edit`} className="text-sm text-[#2C302E] hover:text-[#7A8B7A] truncate">
                    {s.name}
                  </Link>
                  <span className="text-xs text-[#8E958C]">
                    {s.last_response_at ? `last: ${new Date(s.last_response_at).toLocaleDateString()}` : 'never'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card
          title="Surveys with zero responses"
          subtitle="Consider adding a QR code to flyers / intake stations to bootstrap responses."
          accent="warning"
        >
          {data.zero_response_surveys.length === 0 ? (
            <Empty text="Every survey has at least one response." />
          ) : (
            <ul className="divide-y divide-[#E5E0D8]">
              {data.zero_response_surveys.map((s) => (
                <li key={s.id} className="flex items-center justify-between py-2.5">
                  <Link to={`/admin/surveys/${s.id}/edit`} className="text-sm text-[#2C302E] hover:text-[#7A8B7A] truncate">
                    {s.name}
                  </Link>
                  <span className="text-xs text-[#8E958C]">{s.questions_count} questions</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* AI usage */}
      <Card title="AI insights usage" subtitle="Claude-powered summaries generated via the Emergent Universal Key" accent="ai">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MiniStat label="All-time" value={data.ai.total_summaries} />
          <MiniStat label="Last 30 days" value={data.ai.last_30d} />
          <MiniStat label="Last 7 days" value={data.ai.last_7d} />
          <MiniStat
            label="Last used"
            value={data.ai.last_used_at ? new Date(data.ai.last_used_at).toLocaleDateString() : '—'}
            small
          />
        </div>
        {data.ai.last_used_by && (
          <p className="text-xs text-[#8E958C] mt-3">
            Last triggered by <span className="text-[#2C302E]">{data.ai.last_used_by}</span>
          </p>
        )}
        <div className="mt-4 p-3 bg-[#FDF8F4] border border-[#E5D9C8] rounded-lg text-xs text-[#5C635A] flex items-start gap-2">
          <TrendingUp size={14} className="text-[#C67D63] flex-shrink-0 mt-0.5" />
          <span>
            Each summary costs roughly <strong>$0.02–$0.50</strong> in Universal Key credits depending on the
            number of surveys and the amount of free-text data analyzed.
          </span>
        </div>
      </Card>

      {/* Operational health — at-a-glance ops metrics */}
      <Card
        title="Operational health"
        subtitle="Storage, schedules, and team composition — useful for capacity planning."
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MiniStat
            icon={<HardDrive size={14} className="text-[#5C635A]" />}
            label="Uploads on disk"
            value={fmtBytes(data.storage.bytes)}
            small
          />
          <MiniStat
            icon={<FileText size={14} className="text-[#5C635A]" />}
            label="Upload files"
            value={data.storage.file_count}
          />
          <MiniStat
            icon={<Calendar size={14} className="text-[#5C635A]" />}
            label="Scheduled email reports"
            value={t.scheduled_reports}
          />
          <MiniStat
            icon={<UsersRound size={14} className="text-[#5C635A]" />}
            label="Active groups"
            value={t.groups}
          />
          <MiniStat label="Avg. questions / survey" value={t.average_questions_per_survey} small />
          <MiniStat label="Shared (tagged) surveys" value={t.shared_surveys} />
          <MiniStat label="New users — 30d" value={t.new_users_30d} />
          <MiniStat label="New users — 90d" value={t.new_users_90d} />
        </div>
      </Card>
    </div>
  );
};

// ---- small components ----------------------------------------------------

const Stat = ({ icon, label, value, hint }) => (
  <div className="bg-white border border-[#E5E0D8] rounded-xl p-4">
    <div className="flex items-center gap-2 text-[#5C635A] text-sm">
      {icon}
      <span>{label}</span>
    </div>
    <div className="text-3xl font-light text-[#2C302E] mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {value}
    </div>
    {hint && <div className="text-xs text-[#8E958C] mt-1">{hint}</div>}
  </div>
);

const Card = ({ title, subtitle, children, accent }) => {
  const accentClass = accent === 'warning'
    ? 'border-[#E5D9C8] bg-[#FDF8F4]'
    : accent === 'ai'
      ? 'border-[#E5D9C8] bg-gradient-to-br from-[#FDF8F4] to-white'
      : 'border-[#E5E0D8] bg-white';
  return (
    <div className={`rounded-xl border p-5 ${accentClass}`}>
      <div className="mb-4 flex items-start gap-2">
        {accent === 'warning' && <AlertTriangle size={16} className="text-[#C67D63] flex-shrink-0 mt-1" />}
        {accent === 'ai' && <Sparkles size={16} className="text-[#C67D63] flex-shrink-0 mt-1" />}
        <div>
          <h3 className="text-sm font-medium text-[#2C302E]">{title}</h3>
          {subtitle && <p className="text-xs text-[#8E958C] mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
};

const Empty = ({ text }) => (
  <p className="text-sm text-[#8E958C] py-6 text-center">{text}</p>
);

const MiniStat = ({ label, value, small, icon }) => (
  <div className="bg-white border border-[#E5E0D8] rounded-lg p-3">
    <div className="text-[11px] text-[#8E958C] uppercase tracking-wide flex items-center gap-1.5">
      {icon}
      <span>{label}</span>
    </div>
    <div className={`mt-1 text-[#2C302E] ${small ? 'text-base font-medium' : 'text-2xl font-light'}`}>
      {value}
    </div>
  </div>
);

export default InsightsTab;
